// ########################## New code with bar and table #####################################
/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import styles from "./miniBarTableComp.module.css";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    Title,
} from "chart.js";

import { Bar } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    Title
);

const BarChartAndTable = ({ title, columns, data, DataTitle = "" }) => {

    const [showPopup, setShowPopup] = useState(false);
    // const [viewMode, setViewMode] = useState("table");
    const [viewMode, setViewMode] = useState("chart");
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });
    const [sortedData, setSortedData] = useState([]);

    useEffect(() => {
        setSortedData([...data]);
    }, [data]);

    useEffect(() => {
        if (sortConfig.key) {
            const newSortedData = [...data].sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (typeof aValue === "number" && typeof bValue === "number") {
                    return sortConfig.direction === "asc"
                        ? aValue - bValue
                        : bValue - aValue;
                }

                const aString = String(aValue || "");
                const bString = String(bValue || "");

                return sortConfig.direction === "asc"
                    ? aString.localeCompare(bString)
                    : bString.localeCompare(aString);
            });

            setSortedData(newSortedData);
        } else {
            setSortedData([...data]);
        }
    }, [sortConfig, data]);

    const handleSort = (key) => {
        if (sortConfig.key === key) {
            if (sortConfig.direction === "asc") {
                setSortConfig({ key, direction: "desc" });
            } else {
                setSortConfig({ key: null, direction: "asc" });
            }
        } else {
            setSortConfig({ key, direction: "asc" });
        }
    };

    const columnTotals = useMemo(() => {
        const totals = {};
        const dataToUse = sortConfig.key ? sortedData : data;

        columns.forEach((col) => {
            totals[col.key] = 0;
        });

        dataToUse.forEach((row) => {
            columns.forEach((col) => {
                const value = row[col.key];

                if (typeof value === "number" && !isNaN(value)) {
                    totals[col.key] += value;
                }
            });
        });

        Object.keys(totals).forEach((key) => {
            totals[key] = parseFloat(totals[key].toFixed(2));
        });

        return totals;
    }, [columns, data, sortedData, sortConfig]);

    const top10VendorColors = [
        "#7F1D1D",
        "#991B1B",
        "#B91C1C",
        "#DC2626",
        "#EF4444",
        "#F97316",
        "#FB923C",
        "#FACC15",
        "#f5d96a",
        "#fae48a"
    ];

    const chartData = useMemo(() => {
        const labelColumn = columns.find(
            (col) => typeof data[0]?.[col.key] === "string"
        );
        const valueColumn = columns.find(
            (col) => typeof data[0]?.[col.key] === "number"
        );
        if (!labelColumn || !valueColumn) return null;
        const labels = data.map((row) => row[labelColumn.key]);
        const values = data.map((row) => row[valueColumn.key]);

        return {
            labels,
            datasets: [
                {
                    label: valueColumn.header,
                    data: values,
                    backgroundColor: values.map((_, i) => {
                        return top10VendorColors[i] || "#9CA3AF";
                    }),
                    borderRadius: 4,
                    barThickness: 18,
                },
            ],
        };
    }, [columns, data]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    usePointStyle: true,
                    pointStyle: "circle",
                    boxWidth: 12,
                    boxHeight: 12,
                    font: {
                        size: window.innerWidth < 768 ? 8 : 10,
                    },
                },
            },
            datalabels: {
                color: "#000",
                anchor: (context) => {
                    const value = context.dataset.data[context.dataIndex];
                    return value >= 0 ? "end" : "start";
                },
                align: (context) => {
                    const value = context.dataset.data[context.dataIndex];
                    return value >= 0 ? "top" : "bottom";
                },
                font: {
                    weight: "bold",
                    size: window.innerWidth < 768 ? 7 : 8,
                },
                formatter: (value) => value,
            }
        },
        scales: {
            x: {
                ticks: {
                    autoSkip: true,
                    maxRotation: 45,
                    minRotation: 0,
                    font: {
                        size: window.innerWidth < 768 ? 8 : 10,
                    },
                },
                grid: { display: false },
            },
            // y: {
            //     beginAtZero: true,
            // },
            y: {
                beginAtZero: true,
            },

        },
    };

    const downloadTableAsCSV = () => {
        const dataToDownload = sortConfig.key ? sortedData : data;
        const headers = columns.map((col) => `"${col.header}"`).join(",");
        const rows = dataToDownload.map((row) => {
            return columns
                .map((col) => `"${row[col.key]}"`)
                .join(",");
        });

        const totalRow = columns
            .map((col, index) => {
                if (index === 0) return '"Total"';

                if (
                    typeof columnTotals[col.key] === "number" &&
                    columnTotals[col.key] !== 0
                ) {
                    return `"${columnTotals[col.key]}"`;
                }

                return '""';
            })
            .join(",");

        const csvContent = [headers, ...rows, totalRow].join("\n");

        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });

        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);

        link.setAttribute(
            "download",
            `${title.replace(/\s+/g, "_")}.csv`
        );

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className={styles.sort_icon}>▲</span>;
        }

        if (sortConfig.direction === 'asc') {
            return <span className={styles.sort_icon_active}>▲</span>;
        }

        return <span className={styles.sort_icon_active}>▼</span>;
    };

    const renderTable = () => (
        <div className={styles.table_scroll}>
            <table className={styles.custom_table}>
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th
                                key={index}
                                onClick={() => col.sortable && handleSort(col.key)}
                            >
                                {col.header}
                                {col.sortable && renderSortIcon(col.key)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(sortConfig.key ? sortedData : data).map((row, i) => (
                        <tr key={i}>
                            {columns.map((col, j) => (
                                <td key={j}>{row[col.key]}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className={styles.total_row}>
                        {columns.map((col, i) => (
                            <td key={i}>
                                {typeof columnTotals[col.key] === "number"
                                    ? columnTotals[col.key]
                                    : i === 0
                                        ? "Total"
                                        : ""}
                            </td>
                        ))}
                    </tr>
                </tfoot>
            </table>
        </div>
    );

    const renderChart = () => (
        <div style={{ height: "190px", width: "100%" }}>
            {chartData && (
                <Bar data={chartData} options={chartOptions} />
            )}
        </div>
    );

    return (
        <>
            <div className={styles.table_container}>
                <div className={styles.table_header_container}>
                    <div className={styles.button_group}>
                        <button
                            className={styles.popup_button}
                            onClick={() => setShowPopup(true)}
                        >
                            &#8505;
                        </button>
                        <button
                            className={styles.download_button}
                            onClick={downloadTableAsCSV}
                        >
                            &#11123;
                        </button>
                    </div>
                    <div
                        className={styles.table_header_title}
                        title={DataTitle}
                    >
                        {title}
                    </div>
                    <div className={styles.toggle_container}>
                        <span className={styles.toggle_label}>
                            Table
                        </span>
                        <label className={styles.switch}>
                            <input
                                type="checkbox"
                                checked={viewMode === "chart"}
                                onChange={(e) =>
                                    setViewMode(e.target.checked ? "chart" : "table")
                                }
                            />
                            <span className={`${styles.slider} ${styles.round}`}></span>
                        </label>
                        <span className={styles.toggle_label}>
                            Chart
                        </span>
                    </div>
                </div>
                {viewMode === "table" ? renderTable() : renderChart()}
            </div>
            {showPopup && (
                <div className={styles.popup_overlay}>
                    <div className={styles.popup_container}>
                        <div className={styles.popup_header}>
                            <h3>{title}</h3>
                            <div className={styles.toggle_container} style={{ marginRight: "12px" }}>
                                <span className={styles.toggle_label}>Table</span>
                                <label className={styles.switch}>
                                    <input
                                        type="checkbox"
                                        checked={viewMode === "chart"}
                                        onChange={(e) =>
                                            setViewMode(e.target.checked ? "chart" : "table")
                                        }
                                    />
                                    <span className={`${styles.slider} ${styles.round}`}></span>
                                </label>
                                <span className={styles.toggle_label}>Chart</span>
                            </div>
                            <button
                                className={styles.download_button_popup}
                                onClick={downloadTableAsCSV}
                                title="Download table as CSV"
                            >
                                &#11123;
                            </button>
                            <button
                                className={styles.close_button}
                                onClick={() => setShowPopup(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className={styles.popup_content}>
                            {viewMode === "table"
                                ? renderTable()
                                : renderChart()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BarChartAndTable;

// ############################# new responsive code #########################
